import * as React from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import { AuthGuard } from "@/components/auth/auth-guard";
import { Layout as DashboardLayout } from "@/components/dashboard/layout/layout";

// Import all components directly instead of using dynamic imports
import { Dashboard } from "@/new-pages/dashboard/dashboard";
import { Accounts } from "@/new-pages/dashboard/accounts";
import { UserManagement } from "@/new-pages/dashboard/accounts/user-management";
import { Users } from "@/new-pages/dashboard/users";
import { Agents } from "@/new-pages/dashboard/agents";
import { CreateAgent } from "@/new-pages/dashboard/agents/create";
import { Personas } from "@/new-pages/dashboard/personas";
import { CreatePersona } from "@/new-pages/dashboard/personas/create";
import { ProvingGroundRuns } from "@/new-pages/dashboard/proving-ground/runs";
import { ProvingGroundRunView } from "@/new-pages/dashboard/proving-ground/runs/view";
import { ProvingGroundScenarios } from "@/new-pages/dashboard/proving-ground/scenarios";
import { ProvingGroundScorecard } from "@/new-pages/dashboard/proving-ground/scorecard";
import { ProvingGroundSettingsPage } from "@/new-pages/dashboard/proving-ground/settings";
import { Contacts } from "@/new-pages/dashboard/contacts";
import { Fields } from "@/new-pages/dashboard/contacts/fields";
import { Groups } from "@/new-pages/dashboard/contacts/groups";
import { FindLeads } from "@/new-pages/dashboard/contacts/find-leads";
import { PhoneNumbers } from "@/new-pages/dashboard/phoneNumbers";
import { CallLogs } from "@/new-pages/dashboard/call-logs";
import { Campaigns } from "@/new-pages/dashboard/campaigns";
import { CreateCampaign } from "@/new-pages/dashboard/campaigns/create";
import { CampaignView } from "@/new-pages/dashboard/campaigns/view";
import { PromptSettings } from "@/new-pages/dashboard/admin/prompt-settings";
import { Voices } from "@/new-pages/dashboard/admin/voices";
import { CampaignRequests } from "@/new-pages/dashboard/campaign-requests";
import { AdminQueue } from "@/new-pages/dashboard/campaign-requests/admin";
import { RequestPreparation } from "@/new-pages/dashboard/campaign-requests/admin/preparation";
import { CreateCampaignRequest } from "@/new-pages/dashboard/campaign-requests/create";
import { CampaignRequestLive } from "@/new-pages/dashboard/campaign-requests/live";
import { CampaignRequestView } from "@/new-pages/dashboard/campaign-requests/view";
import { PriceBook } from "@/new-pages/dashboard/campaign-requests/price-book";
import { CampaignBriefs } from "@/new-pages/campaign-briefs";
import { CampaignBriefView } from "@/new-pages/campaign-briefs/view";
import { ClientRequests } from "@/new-pages/client-requests";
import { ClientRequestView } from "@/new-pages/client-requests/view";
import { RateCard } from "@/new-pages/rate-card";
import { Plugins } from "@/new-pages/dashboard/plugins";
import { Services } from "@/new-pages/dashboard/plugins/services";
import { SystemLogs } from "@/new-pages/dashboard/systemLogs";
import { ContactView } from "@/new-pages/dashboard/contacts/view";
import { LeadView } from "@/new-pages/dashboard/campaigns/view/lead-list/view";
import { TwoFactorSettings } from "@/new-pages/dashboard/settings/two-factor";
import { DoNotCall } from "@/new-pages/dashboard/settings/do-not-call";
import { PlatformSettings } from "@/new-pages/dashboard/settings/platform-settings";
import { WebsiteIntake } from "@/new-pages/dashboard/settings/website-intake";
import { WebsiteIntakeRequests } from "@/new-pages/dashboard/settings/website-intake/requests";

function FormerPlatformPath() {
	const { "*": rest = "" } = useParams();
	const { search, hash } = useLocation();
	return <Navigate replace to={{ pathname: `/platform/${rest}`, search, hash }} />;
}

export const route: RouteObject = {
	element: (
		<AuthGuard>
			<DashboardLayout>
				<Outlet />
			</DashboardLayout>
		</AuthGuard>
	),
	children: [
		{
			index: true,
			Component: Dashboard,
		},
		{
			path: "accounts",
			Component: Accounts,
		},
		{
			path: "accounts/:accountId/users",
			Component: UserManagement,
		},
		{
			path: "user-management",
			Component: UserManagement,
		},
		{
			path: "users",
			Component: Users,
		},
		{
			path: "agents",
			Component: Agents,
		},
		{
			path: "agents/create",
			Component: CreateAgent,
		},
		{
			path: "agents/:agentId",
			Component: CreateAgent,
		},
		{
			path: "personas",
			Component: Personas,
		},
		{
			path: "personas/create",
			Component: CreatePersona,
		},
		{
			path: "personas/:personaId",
			Component: CreatePersona,
		},
		{
			path: "proving-ground/runs",
			Component: ProvingGroundRuns,
		},
		{
			path: "proving-ground/runs/:runId",
			Component: ProvingGroundRunView,
		},
		{
			path: "proving-ground/scenarios",
			Component: ProvingGroundScenarios,
		},
		{
			path: "proving-ground/scorecard",
			Component: ProvingGroundScorecard,
		},
		{
			path: "proving-ground/settings",
			Component: ProvingGroundSettingsPage,
		},
		{
			path: "contacts",
			Component: Contacts,
		},
		{
			path: "contacts/:contactId/view",
			Component: ContactView,
		},
		{
			path: "contacts/fields",
			Component: Fields,
		},
		{
			path: "contacts/groups",
			Component: Groups,
		},
		{
			path: "contacts/find-leads",
			Component: FindLeads,
		},
		{
			path: "phone-numbers",
			Component: PhoneNumbers,
		},
		{
			path: "call-logs",
			Component: CallLogs,
		},
		{
			path: "campaigns",
			Component: Campaigns,
		},
		{
			path: "campaigns/:campaignId",
			Component: CreateCampaign,
		},
		{
			path: "campaigns/create",
			Component: CreateCampaign,
		},
		{
			path: "campaigns/:campaignId/view",
			Component: CampaignView,
		},
		{
			path: "campaigns/:campaignId/contacts/:leadId/view",
			Component: LeadView,
		},
		{
			path: "campaign-briefs",
			Component: CampaignBriefs,
		},
		{
			path: "campaign-briefs/:briefId",
			Component: CampaignBriefView,
		},
		{
			path: "client-requests",
			Component: ClientRequests,
		},
		{
			path: "client-requests/:briefId",
			Component: ClientRequestView,
		},
		{
			path: "rate-card",
			Component: RateCard,
		},
		{
			// Static segments outrank the `:requestId` param, so a later `create` keeps resolving
			// to its own screen rather than being read as an id.
			path: "campaign-requests",
			Component: CampaignRequests,
		},
		{
			/*
			 * The price book has never lived here — it is public-schema and account-less, so it
			 * belongs in the super-admin group at `/platform/price-book`. This path existed anyway,
			 * as a hole rather than a route: with no static segment to match, it falls through to
			 * `:requestId`, the screen asks the API for a request called "price-book", and the
			 * client is shown a bare red "could not be loaded" with no way out. A stale link is
			 * not an error, so it redirects to where the screen actually is rather than reporting
			 * a request that was never missing.
			 */
			path: "campaign-requests/price-book",
			element: <Navigate replace to="/platform/price-book" />,
		},
		{
			// The wizard reads `?step=N` and `?request=<id>` from the query rather than the path,
			// so "save & exit" hands back a link that resumes exactly where it left off.
			path: "campaign-requests/create",
			Component: CreateCampaignRequest,
		},
		{
			path: "campaign-requests/:requestId",
			Component: CampaignRequestView,
		},
		{
			// The programme rollup. Keyed by the request rather than the programme so the client
			// only ever needs the one id — the screen reads `campaignProgrammeId` off the request
			// and says so plainly when nothing has been provisioned yet.
			path: "campaign-requests/:requestId/live",
			Component: CampaignRequestLive,
		},
		{
			// Super-admin surfaces. Guarded by the API — the queue reads a public-schema projection
			// no account-scoped role can see — and hidden from the nav by `isSuperAdmin`.
			path: "platform/campaign-requests",
			Component: AdminQueue,
		},
		{
			path: "platform/campaign-requests/:requestId",
			Component: RequestPreparation,
		},
		{
			path: "platform/prompt-settings",
			Component: PromptSettings,
		},
		{
			path: "platform/price-book",
			Component: PriceBook,
		},
		{
			path: "platform/voices",
			Component: Voices,
		},
		{
			path: "plugins",
			Component: Plugins,
		},
    {
      path: "plugins/services",
      Component: Services,
    },
		{
			path: "system-logs",
			Component: SystemLogs,
		},
		{
			path: "settings/two-factor",
			Component: TwoFactorSettings,
		},
		{
			path: "settings/do-not-call",
			Component: DoNotCall,
		},
		{
			path: "settings/website-intake",
			Component: WebsiteIntake,
		},
		{
			path: "settings/website-intake/requests",
			Component: WebsiteIntakeRequests,
		},
		{
			path: "platform/settings",
			Component: PlatformSettings,
		},
		{
			// Where the super-admin screens lived before `/platform`: under the panel's own `/admin`
			// base they read `/admin/admin/...`. Kept so a bookmark or an open tab still lands.
			path: "admin/*",
			Component: FormerPlatformPath,
		},
		{
			path: "platform-settings",
			element: <Navigate replace to="/platform/settings" />,
		},
	],
};
